/**
 * Canvas Data Bridge
 *
 * Lightweight module-level bridge that lets components outside the
 * Excalidraw component tree (e.g. RecordingTeleprompter) access
 * canvas element data without prop drilling.
 *
 * Usage:
 *   CanvasView registers:  registerCanvasGetter(() => api.getSceneElements())
 *   Consumer calls:        getCanvasElements()
 */

type CanvasElement = Record<string, unknown>;
type CanvasGetter = () => readonly CanvasElement[];
type CanvasImageGetter = () => Promise<Blob>;

let _getter: CanvasGetter | null = null;
let _imageGetter: CanvasImageGetter | null = null;

export function registerCanvasGetter(fn: CanvasGetter): void {
  _getter = fn;
}

export function unregisterCanvasGetter(): void {
  _getter = null;
}

export function getCanvasElements(): readonly CanvasElement[] {
  return _getter?.() ?? [];
}

export function registerCanvasImageGetter(fn: CanvasImageGetter): void {
  _imageGetter = fn;
}

export function unregisterCanvasImageGetter(): void {
  _imageGetter = null;
}

export function getCanvasImage(): Promise<Blob | null> {
  if (!_imageGetter) return Promise.resolve(null);
  return _imageGetter().catch(() => null);
}

/**
 * Build a compact textual summary of canvas elements for LLM context.
 * Extracts text content, element types, and arrow relationships.
 */
export function summarizeCanvasForLLM(
  elements: readonly CanvasElement[],
): string {
  const visible = elements.filter((el) => !el.isDeleted);
  if (visible.length === 0) {
    return "";
  }

  const textContents: string[] = [];
  const typeCounts: Record<string, number> = {};
  const connections: string[] = [];

  for (const el of visible) {
    const elType = (el.type as string) || "unknown";
    typeCounts[elType] = (typeCounts[elType] || 0) + 1;

    if (elType === "text" && el.text) {
      textContents.push(el.text as string);
    }

    // Containers with bound text (rectangle, ellipse, diamond with labels)
    if (el.boundElements && Array.isArray(el.boundElements)) {
      for (const bound of el.boundElements as Array<{
        id: string;
        type: string;
      }>) {
        if (bound.type === "text") {
          const boundText = visible.find((e) => e.id === bound.id);
          if (boundText?.text) {
            textContents.push(boundText.text as string);
          }
        }
      }
    }

    // Arrow labels (start/end text)
    if (elType === "arrow") {
      const startId = (el as any).startBinding?.elementId;
      const endId = (el as any).endBinding?.elementId;
      if (startId && endId) {
        const startEl = visible.find((e) => e.id === startId);
        const endEl = visible.find((e) => e.id === endId);
        const startLabel = findLabelText(startEl, visible);
        const endLabel = findLabelText(endEl, visible);
        if (startLabel && endLabel) {
          connections.push(`${startLabel} -> ${endLabel}`);
        }
      }
    }
  }

  const parts: string[] = [];

  const typeDesc = Object.entries(typeCounts)
    .map(([t, c]) => `${c} ${t}(s)`)
    .join(", ");
  parts.push(`Diagram elements: ${typeDesc}`);

  // Deduplicate text contents (bound text may appear as standalone too)
  const uniqueTexts = [...new Set(textContents)].filter(Boolean);
  if (uniqueTexts.length > 0) {
    parts.push(`Text content:\n${uniqueTexts.map((t) => `- ${t}`).join("\n")}`);
  }

  if (connections.length > 0) {
    parts.push(`Connections:\n${connections.map((c) => `- ${c}`).join("\n")}`);
  }

  return parts.join("\n\n");
}

function findLabelText(
  el: CanvasElement | undefined,
  all: CanvasElement[],
): string | null {
  if (!el) {
    return null;
  }
  // Direct text element
  if (el.type === "text" && el.text) {
    return el.text as string;
  }
  // Container with bound text
  if (el.boundElements && Array.isArray(el.boundElements)) {
    for (const bound of el.boundElements as Array<{
      id: string;
      type: string;
    }>) {
      if (bound.type === "text") {
        const textEl = all.find((e) => e.id === bound.id);
        if (textEl?.text) {
          return textEl.text as string;
        }
      }
    }
  }
  return null;
}
